import { apiClient } from './api';

/**
 * Reports Service
 * Handles all reports-related API calls
 */
export const reportsService = {
  /**
   * Get report templates
   * @returns {Promise} Templates list
   */
  getTemplates: () => {
    return apiClient.get('/reports/templates');
  },

  /**
   * Create report from template
   * @param {Object} data - Template ID and overrides
   * @returns {Promise} Created report
   */
  createFromTemplate: (data) => {
    return apiClient.post('/reports/from-template', data);
  },

  /**
   * Create report
   * @param {Object} data - Report configuration
   * @returns {Promise} Created report
   */
  create: (data) => {
    return apiClient.post('/reports', data);
  },

  /**
   * List reports
   * @param {Object} params - Query parameters (status, account_id, page, per_page)
   * @returns {Promise} Reports list
   */
  list: (params = {}) => {
    return apiClient.get('/reports', params);
  },

  /**
   * Get report by ID
   * @param {number} id - Report ID
   * @returns {Promise} Report data
   */
  get: (id) => {
    return apiClient.get(`/reports/${id}`);
  },

  /**
   * Update report
   * @param {number} id - Report ID
   * @param {Object} data - Updated report configuration
   * @returns {Promise} Updated report
   */
  update: (id, data) => {
    return apiClient.put(`/reports/${id}`, data);
  },

  /**
   * Delete report
   * @param {number} id - Report ID
   * @returns {Promise} Deletion result
   */
  delete: (id) => {
    return apiClient.delete(`/reports/${id}`);
  },

  /**
   * Export report as CSV
   * @param {number} id - Report ID
   * @returns {Promise} CSV file download
   */
  exportCsv: (id) => {
    return apiClient.get(`/reports/${id}/export/csv`, {}, { responseType: 'blob' });
  },

  /**
   * Export report as JSON
   * @param {number} id - Report ID
   * @returns {Promise} JSON file download
   */
  exportJson: (id) => {
    return apiClient.get(`/reports/${id}/export/json`, {}, { responseType: 'blob' });
  },

  /**
   * Export report as PDF
   * @param {number} id - Report ID
   * @returns {Promise} PDF file download
   */
  exportPdf: (id) => {
    return apiClient.get(`/reports/${id}/export/pdf`, {}, { responseType: 'blob' });
  },

  /**
   * Duplicate report
   * @param {number} id - Report ID to duplicate
   * @param {string} newName - Optional new name for the duplicate
   * @returns {Promise} Created duplicate report
   */
  duplicate: async (id, newName = null) => {
    // First, get the original report
    const originalReport = await apiClient.get(`/reports/${id}`);
    
    // Prepare duplicate data
    const duplicateData = {
      report_name: newName || `${originalReport.data.report_name} (Copy)`,
      account_id: originalReport.data.account_id,
      start_date: originalReport.data.start_date,
      end_date: originalReport.data.end_date,
      metrics: originalReport.data.metrics || [],
      breakdowns: originalReport.data.breakdowns || [],
      format: originalReport.data.format || 'both',
      status: 'draft', // Always create duplicates as draft
    };

    // Add filters if they exist
    if (originalReport.data.filters) {
      duplicateData.filters = originalReport.data.filters;
    }

    // Add schedule if it exists (but disable it by default)
    if (originalReport.data.schedule) {
      duplicateData.schedule = {
        ...originalReport.data.schedule,
        enabled: false, // Disable schedule for duplicates
      };
    }

    // Create the duplicate
    return apiClient.post('/reports', duplicateData);
  },
};
